/**
 * Helper functions para manejo de Razas de Bovinos
 * Códigos SIGSA para declaraciones de dispositivos de identificación electrónica
 */

/**
 * Mapeo de códigos de raza a nombres completos
 * Basado en la tabla de razas SIGSA para bovinos
 */
export const RAZA_CODIGO_A_NOMBRE: Record<string, string> = {
  HA: "Holando Argentino",
  PH: "Polled Hereford",
  J: "Jersey",
  LA: "Limangus",
  FS: "Simmental",
  SG: "Santa Gertrudis",
  OR: "Otra Raza",
  L: "Limousine",
  K: "Kiwi",
  BO: "Bosmara",
  SRB: "Sueca Roja y Blanca",
  SA: "Senangus",
  B: "Brahman",
  SH: "Shorthorn",
  SP: "Senepol",
  TL: "Tuli",
  SI: "San Ignacio",
  GC: "Ganado Cruza",
  H: "Hereford",
  W: "Wagyu",
  SF: "Seneford",
  CH: "Charolais",
  AA: "Aberdeen Angus",
  BG: "Brangus",
  BF: "Braford",
  CR: "Criolla",
  MG: "Murray Grey",
  G: "Galloway",
};

/**
 * Mapeo inverso: nombre de raza a código
 */
export const RAZA_NOMBRE_A_CODIGO: Record<string, string> = Object.entries(
  RAZA_CODIGO_A_NOMBRE
).reduce<Record<string, string>>((acc, [codigo, nombre]) => {
  // Crear entradas para diferentes variaciones del nombre
  acc[nombre] = codigo;
  // También agregar versión en minúsculas para búsquedas case-insensitive
  acc[nombre.toLowerCase()] = codigo;
  // Agregar versión sin acentos para búsquedas más flexibles
  acc[nombre.normalize("NFD").replace(/[\u0300-\u036f]/g, "")] = codigo;
  return acc;
}, {} as Record<string, string>);

// Atajos de nombre corto que el productor escribe y el nombre oficial no
// cubre (ej. "Angus" vs "Aberdeen Angus"). Sólo claves NUEVAS: no pisa
// variaciones existentes del mapa oficial.
const ATAJOS_RAZA: Record<string, string> = {
  angus: "AA",
  aberdeen: "AA",
  braford: "BF",
  brangus: "BG",
  holando: "HA",
  jersey: "J",
  limangus: "LA",
  simmental: "FS",
  "santa gertrudis": "SG",
  limousine: "L",
  brahman: "B",
  hereford: "H",
  wagyu: "W",
  shorthorn: "SH",
};
Object.entries(ATAJOS_RAZA).forEach(([atajo, codigo]) => {
  if (RAZA_NOMBRE_A_CODIGO[atajo] === undefined) {
    RAZA_NOMBRE_A_CODIGO[atajo] = codigo;
    RAZA_NOMBRE_A_CODIGO[atajo.charAt(0).toUpperCase() + atajo.slice(1)] = codigo;
  }
});

/**
 * Obtiene el nombre completo de una raza a partir de su código
 * @param codigo - Código de raza SIGSA (ej: "HA", "PH", "AA")
 * @returns Nombre completo de la raza, o el código si no se encuentra
 */
export function getRazaNombre(codigo: string | undefined | null): string {
  if (!codigo) return "";
  return RAZA_CODIGO_A_NOMBRE[codigo] || codigo;
}

/**
 * Obtiene el código de una raza a partir de su nombre
 * @param nombre - Nombre completo de la raza (ej: "Holando Argentino", "Aberdeen Angus")
 * @returns Código SIGSA de la raza, o undefined si no se encuentra
 */
export function getRazaCodigo(nombre: string | undefined | null): string | undefined {
  if (!nombre) return undefined;

  // Buscar directo
  if (RAZA_NOMBRE_A_CODIGO[nombre]) {
    return RAZA_NOMBRE_A_CODIGO[nombre];
  }

  // Buscar en minúsculas
  const nombreLower = nombre.toLowerCase();
  if (RAZA_NOMBRE_A_CODIGO[nombreLower]) {
    return RAZA_NOMBRE_A_CODIGO[nombreLower];
  }

  // Buscar sin acentos
  const nombreNormalizado = nombre
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (RAZA_NOMBRE_A_CODIGO[nombreNormalizado]) {
    return RAZA_NOMBRE_A_CODIGO[nombreNormalizado];
  }

  return resolveRazaCodigoFuzzy(nombre);
}

/** Candidatos \u00fanicos (nombre normalizado \u2192 c\u00f3digo) para la b\u00fasqueda difusa. */
const CANDIDATOS_RAZA_FUZZY: { nombre: string; codigo: string }[] = (() => {
  const vistos = new Set<string>();
  const lista: { nombre: string; codigo: string }[] = [];
  Object.entries(RAZA_NOMBRE_A_CODIGO).forEach(([clave, codigo]) => {
    const nombre = clave
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    const claveUnica = `${nombre}|${codigo}`;
    if (nombre === "" || vistos.has(claveUnica)) return;
    vistos.add(claveUnica);
    lista.push({ nombre, codigo });
  });
  return lista;
})();

const normalizarTextoRaza = (valor: string): string =>
  valor
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Distancia de Levenshtein (DP cl\u00e1sico, cadenas cortas de razas). */
const levenshtein = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const curr = [i, ...Array(n).fill(0)];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = curr;
  }
  return prev[n];
};

/**
 * Resoluci\u00f3n difusa de raza para IMPORTACIONES: el productor escribe
 * abreviaturas ("Angu") o tipea con errores ("Brafod"), y eso tiene que
 * convertir solo contra la tabla SIGSA \u2014 no convertirlo en un problema del
 * usuario. Cadena: c\u00f3digo directo \u2192 prefijo \u00daNICO (\u22654 chars) \u2192 Levenshtein
 * m\u00ednimo \u00fanico dentro de un umbral (\u22641; \u22642 para nombres largos).
 * Ambiguo o lejos de todo \u2192 undefined (queda el error corregible del preview:
 * nunca adivinamos una conversi\u00f3n).
 */
export function resolveRazaCodigoFuzzy(valor: string | undefined | null): string | undefined {
  if (!valor) return undefined;
  const raw = normalizarTextoRaza(valor);
  if (raw === "") return undefined;

  // Ya es un c\u00f3digo SIGSA ('aa' \u2192 'AA')
  const codigoDirecto = valor.trim().toUpperCase();
  if (RAZA_CODIGO_A_NOMBRE[codigoDirecto]) return codigoDirecto;

  // Prefijo \u00fanico: "angu" \u2192 angus / aberdeen angus (ambos AA) \u2713
  if (raw.length >= 4) {
    const prefijos = new Set(
      CANDIDATOS_RAZA_FUZZY.filter(c => c.nombre.length > raw.length && c.nombre.startsWith(raw))
        .map(c => c.codigo)
    );
    if (prefijos.size === 1) return [...prefijos][0];
  }

  // Typo cercano \u00fanico: "brafod" \u2192 braford (dist 1)
  const umbral = raw.length >= 7 ? 2 : 1;
  let mejor: { codigo: string; dist: number } | undefined;
  let empate = false;
  CANDIDATOS_RAZA_FUZZY.forEach(c => {
    const dist = levenshtein(raw, c.nombre);
    if (dist > umbral) return;
    if (!mejor || dist < mejor.dist) {
      mejor = { codigo: c.codigo, dist };
      empate = false;
    } else if (dist === mejor.dist && c.codigo !== mejor.codigo) {
      empate = true;
    }
  });
  if (mejor && !empate) return mejor.codigo;

  return undefined;
}

/**
 * Convierte un valor de raza a código SIGSA
 * Si el valor ya es un código conocido, lo devuelve tal cual
 * Si es un nombre de raza, busca el código correspondiente
 * @param valor - Código o nombre de raza
 * @returns Código SIGSA, o el valor original si no se puede convertir
 */
export function normalizarRazaCodigo(valor: string | undefined | null): string | undefined {
  if (!valor) return undefined;

  // Si ya es un código conocido (validar que esté en mayúsculas)
  const valorUpper = valor.toUpperCase().trim();
  if (RAZA_CODIGO_A_NOMBRE[valorUpper]) {
    return valorUpper;
  }

  // Si es un nombre, buscar el código
  return getRazaCodigo(valor);
}

/**
 * Lista de todas las razas disponibles como array de objetos
 * Útil para dropdowns y selectores
 */
export const RAZAS_LISTA = Object.entries(RAZA_CODIGO_A_NOMBRE).map(
  ([codigo, nombre]) => ({
    codigo,
    nombre,
    label: `${codigo} - ${nombre}`, // Para mostrar en select
  })
);

/**
 * Razas ordenadas alfabéticamente por nombre
 */
export const RAZAS_ORDENADAS = [...RAZAS_LISTA].sort((a, b) =>
  a.nombre.localeCompare(b.nombre, "es")
);
